import { Injectable } from '@angular/core';

import { Personal_Document } from '../models/personal_document.model';

import { v4 } from 'uuid';
import { DocumentService, UploadReturn } from './base/document.service';

@Injectable({
  providedIn: 'root',
})
export class PersonalDocumentService extends DocumentService<Personal_Document> {
  constructor() {
    super('cp_personal_document');
    // route create/update through lambdas
    this.lambdaNames = { create: 'create_document', update: 'update_document' };
  }

  public async upload(
    f: File,
    user_id: string,
    name: string,
    bucket: string,
    seq?: number,
    file_metadata?: any,
  ): Promise<UploadReturn> {
    const ext = f.name.split('.').pop();
    const id = v4();
    const url =
      seq === undefined
        ? `${user_id}/${id}.${ext}`
        : `${user_id}/${id}/${seq}.${ext}`;
    const pd: Personal_Document = {
      bucket,
      doc_type: name,
      kv_extract: {},
      ocr_extract: {},
      status: 'PRE_UPLOAD',
      url,
      user_id: user_id,
      created_at: new Date(),
      id,
      file_metadata: file_metadata ?? null,
    };

    const ulret = await this.uploadDocument(pd,f);

    if (ulret.error || !ulret.data) {
      return { data: null, error: ulret.error };
    }

    return {
      data: {
        doc: pd,
        path: ulret.data.path,
      },
      error: null,
    };
  }
  public async uploadV2(
    f: File,
    user_id: string,
    name: string,
    bucket: string,
    options: {
      seq?: number;
      check_id?: string;
    } = {}
  ): Promise<UploadReturn> {
    const ext = f.name.split('.').pop();
    const id = v4();
    const url =
      options.seq === undefined
        ? `${user_id}/${id}.${ext}`
        : `${user_id}/${id}/${options.seq}.${ext}`;
    const pd: Personal_Document = {
      bucket,
      doc_type: name,
      kv_extract: {},
      ocr_extract: {},
      status: 'PRE_UPLOAD',
      url,
      user_id: user_id,
      created_at: new Date(),
      check_id: options.check_id,
      id,
    };

    console.log('uploadV2 bef', pd);
    const ulret = await this.uploadDocumentV2(pd, f);
    console.log('uploadV2 af', ulret);

    if (ulret.error || !ulret.data) {
      return { data: null, error: ulret.error };
    }

    return {
      data: {
        doc: pd,
        path: ulret.data.path,
      },
      error: null,
    };
  }

  /**
   * Fetch documents by id.
   *
   * Prefer this over {@link getAllDocumentsFromCheckID} when the ids are already known from a
   * check's `requested_attributes.cpPersonalDocumentsId`: `check_id` is re-pointed whenever a
   * document is reused by a newer check, so the reverse lookup follows the latest attempt
   * while the ids on each check stay accurate.
   */
  async getByIds(ids: string[]): Promise<Personal_Document[]> {
    const wanted = ids.filter((id) => !!id);
    if (wanted.length === 0) return [];

    const items = await this.supabase.from(this.table).select().in('id', wanted);
    return items.data || [];
  }

  async getAllDocumentsFromCheckID(
    check_id: string
  ): Promise<Personal_Document[]> {
    const items = await this.supabase
      .from(this.table)
      .select()
      .eq('check_id', check_id);
    return items.data || [];
  }

  public async getPreviewURL(docval: Personal_Document): Promise<string> {
    // 1) Try quarantine first — files there are stored under "{originalBucket}/{originalPath}"
    const quarantinePath = `${docval.bucket}/${docval.url}`;
    const quarantineRes = await this.supabase.storage
      .from('quarantine')
      .createSignedUrl(quarantinePath, 3600);
    if (!quarantineRes.error && quarantineRes.data?.signedUrl) {
      return quarantineRes.data.signedUrl;
    }

    // 2) Fall back to the original bucket
    const { data, error } = await this.supabase.storage
      .from(docval.bucket)
      .createSignedUrl(docval.url, 3600);
    if (error || !data?.signedUrl) {
      throw new Error('Could not get signed url');
    }
    return data.signedUrl;
  }
}
